import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Zap, LayoutTemplate, Send, MousePointerClick, BarChart3, Edit3, Type, Image as ImageIcon, Palette } from 'lucide-react';
import { Button } from '../ui/button';

const EmailCampaignsSlide = () => {
  return (
    <div className="h-full w-full bg-slate-950 flex items-center justify-center p-6 lg:p-12 relative overflow-hidden font-sans text-white">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-[1000px] h-[1000px] bg-blue-600/10 rounded-full blur-[120px] -translate-y-1/2 -translate-x-1/2" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-indigo-600/10 rounded-full blur-[120px] translate-y-1/2 translate-x-1/2" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>
        
        <div className="flex flex-row items-center gap-12 lg:gap-24 max-w-7xl mx-auto z-10 w-full">
            
            {/* Left Content */}
            <div className="flex-1 space-y-8 lg:space-y-10 min-w-0">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-400 text-sm font-bold border border-blue-500/20 shadow-[0_0_20px_-5px_rgba(59,130,246,0.3)]">
                    <Mail className="w-4 h-4" />
                    <span>Email Campaigns</span>
                </div>

                <div className="space-y-4">
                    <h2 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight">
                        Beautiful Emails. <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-cyan-400">Higher Engagement.</span>
                    </h2>

                    <p className="text-lg lg:text-xl text-slate-300 leading-relaxed max-w-xl font-light">
                        Move beyond plain text. Our marketing suite includes "Next Gen Campaigns"—modern, interactive email templates designed to convert patients.
                    </p>
                </div>

                <div className="space-y-6">
                    {[
                        { icon: LayoutTemplate, title: "Drag & Drop Builder", desc: "Customize templates easily or build from scratch." },
                        { icon: Zap, title: "Next Gen Interactivity", desc: "Embedded booking links, surveys, and more." },
                        { icon: BarChart3, title: "Smart Tracking", desc: "See exactly who opens, clicks, and books." }
                    ].map((item, i) => (
                        <div key={i} className="flex gap-5 group items-start">
                            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-slate-900 flex items-center justify-center text-blue-400 border border-slate-800 shadow-lg group-hover:scale-110 transition-transform duration-300 group-hover:border-blue-500/30 shrink-0">
                                <item.icon className="w-6 h-6 lg:w-7 lg:h-7" />
                            </div>
                            <div>
                                <h4 className="text-lg lg:text-xl font-bold text-white group-hover:text-blue-300 transition-colors">{item.title}</h4>
                                <p className="text-slate-400 text-sm lg:text-base leading-relaxed">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Visual - Abstract Email Builder */}
            <motion.div 
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="flex-1 w-full max-w-lg lg:max-w-xl shrink-0 relative perspective-1000"
            >
                {/* Floating Toolbar - Left */}
                <motion.div 
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="absolute -left-12 top-20 flex flex-col gap-3 z-20"
                >
                     {[Edit3, Type, ImageIcon, Palette].map((Icon, i) => (
                        <div key={i} className="w-10 h-10 rounded-full bg-slate-800/80 backdrop-blur-md border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:bg-blue-600 hover:border-blue-500 transition-all shadow-lg cursor-pointer">
                            <Icon className="w-5 h-5" />
                        </div>
                     ))}
                </motion.div>

                {/* Main Email Card */}
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative z-10 transform rotate-y-[-5deg] rotate-x-[5deg] hover:rotate-0 transition-transform duration-500 border border-slate-800">
                    
                    {/* Email Header */}
                    <div className="h-48 bg-slate-900 relative overflow-hidden flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-900 opacity-90"></div>
                        <div className="relative z-10 text-center">
                             <div className="inline-block px-3 py-1 bg-white/10 rounded-full text-[10px] font-bold tracking-widest text-white mb-3 uppercase">Summer Special</div>
                             <h1 className="text-4xl font-black text-white tracking-tighter">BRIGHTER<br/>SMILES</h1>
                        </div>
                        {/* Decorative Circles */}
                        <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-400/30 rounded-full blur-2xl"></div>
                        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-indigo-400/30 rounded-full blur-2xl"></div>
                    </div>

                    {/* Email Body */}
                    <div className="p-8 bg-white space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                             <div className="w-10 h-10 rounded-full bg-slate-100 animate-pulse"></div>
                             <div className="space-y-2 flex-1">
                                 <div className="h-3 w-1/3 bg-slate-100 rounded animate-pulse"></div>
                                 <div className="h-2 w-1/4 bg-slate-50 rounded"></div>
                             </div>
                        </div>

                        <div className="space-y-3">
                            <div className="h-3 w-full bg-slate-100 rounded"></div>
                            <div className="h-3 w-full bg-slate-100 rounded"></div>
                            <div className="h-3 w-5/6 bg-slate-100 rounded"></div>
                        </div>

                        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between group cursor-pointer hover:border-blue-200 transition-colors">
                            <div>
                                <div className="text-xs font-bold text-slate-400 uppercase">Offer</div>
                                <div className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors">Free Whitening Kit</div>
                            </div>
                            <div className="text-2xl font-black text-slate-200 group-hover:text-blue-200 transition-colors">$0</div>
                        </div>

                        <Button className="w-full bg-slate-900 hover:bg-blue-600 text-white font-bold py-6 text-lg rounded-xl shadow-xl transition-all">
                            Claim Offer
                        </Button>
                    </div>
                </div>

                {/* Floating Send Card - Bottom Right */}
                <motion.div 
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="absolute -bottom-6 -right-6 z-30"
                >
                    <div className="bg-slate-800/90 backdrop-blur-xl border border-slate-700 p-4 rounded-2xl shadow-2xl flex flex-col gap-3 w-64">
                         <div className="flex items-center justify-between text-xs font-medium text-slate-400">
                             <span>Campaign Ready</span>
                             <span className="text-green-400">100%</span>
                         </div>
                         <div className="h-1.5 w-full bg-slate-700 rounded-full overflow-hidden">
                             <div className="h-full bg-green-400 w-full rounded-full"></div>
                         </div>
                         <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg mt-1">
                             <Send className="w-3.5 h-3.5 mr-2" /> Launch Now
                         </Button>
                    </div>
                </motion.div>

                {/* Decorative Elements */}
                <div className="absolute -z-10 top-10 right-10 w-full h-full bg-blue-500/20 rounded-2xl blur-3xl"></div>

            </motion.div>
        </div>
    </div>
  );
};

export default EmailCampaignsSlide;
